/* """"""""""""""""""""""""""""""""""""""""""""""""""""""""""""""
   ShopMallEdit.jsx  "  Mall Page Builder (v2)
   Visual theme gallery . Categorised section picker . Improved
   product picker . Sale / clearance helpers . Better UX
   """""""""""""""""""""""""""""""""""""""""""""""""""""""""""""" */
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import {
  getMyShopMallPage,
  updateMyShopMallPage,
  getMyShopProducts,
  toAbsUrl,
  uploadShopMallAsset,
} from "../utils/api";
import { SIZE_RE as SHARED_SIZE_RE } from "../utils/sizeUtils";
import ShopMallPreview from "./ShopMallPreview";
import {
  THEMES,
  SECTION_META,
} from "../components/mall-theme/SectionComponents";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Save,
  Trash2,
  GripVertical,
  Palette,
  Type,
  Image as ImageIcon,
  Link as LinkIcon,
  ShoppingBag,
  User,
  Menu,
  Smartphone,
  Monitor,
  Copy,
  Check,
  ChevronDown,
  Plus,
  Search,
  X,
  Star,
  Flame,
  Clock,
  Megaphone,
  Quote,
  Play,
  Minus,
  Grid3X3,
  Eye,
  Settings,
  Layers,
  Sparkles,
  AlertCircle,
  PanelLeft,
  ChevronRight,
  Percent,
  FolderPlus,
  Tag,
} from "lucide-react";

/* 
   HELPERS
    */
const simpleId = () => Math.random().toString(36).substr(2, 9);

/* Countdown end date: stored as ISO (UTC); the datetime-local input needs local "YYYY-MM-DDTHH:mm". */
function isoToLocalInput(v) {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function localInputToIso(v) {
  if (!v) return "";
  const d = new Date(v); // datetime-local value is parsed as local time
  return Number.isNaN(d.getTime()) ? "" : d.toISOString();
}

/* Load every product of this shop (the API pages; default page size is 24). */
async function loadAllShopProducts() {
  const all = [];
  const LIMIT = 500;
  for (let page = 1; page <= 50; page++) {
    const data = await getMyShopProducts({ page, limit: LIMIT });
    const items = Array.isArray(data) ? data : data?.items || data?.products || [];
    all.push(...items);
    if (Array.isArray(data) || !data?.hasMore || items.length === 0) break;
  }
  return all;
}

const ICON_MAP = {
  User,
  Image: ImageIcon,
  Grid3X3,
  Star,
  Flame,
  Megaphone,
  Clock,
  Quote,
  Link: LinkIcon,
  Type,
  Images: ImageIcon,
  Play,
  Minus,
  Menu,
};

const INITIAL_SECTIONS = [
  {
    id: "start-1",
    type: "ProfileHeader",
    props: { shopName: "", bio: ""},
  },
];

/* Section default props for each type */
const getDefaultProps = (type) => {
  const defaults = {
    ProfileHeader: { shopName: "", bio: "" },
    HeroBanner: { title: "Welcome", subtitle: "Discover our collection", imageUrl: "", buttonText: "Shop Now", buttonLink: "" },
    ProductGrid: { title: "Our Products", productIds: [] },
    FeaturedProduct: { title: "", subtitle: "", productIds: [], buttonText: "Shop Now", buttonLink: "", badge: "" },
    SaleBanner: { title: "Big Sale!", subtitle: "Limited time offer", discount: "Up to 50% OFF", bannerStyle: "gradient", buttonText: "Shop Sale", buttonLink: "" },
    AnnouncementBar: { text: "Free shipping on orders over $50!", barStyle: "primary", link: "" },
    Countdown: { title: "Sale Ends In", endDate: "" },
    Testimonials: { title: "What Our Customers Say", testimonials: [{ name: "Happy Customer", text: "Great products and fast shipping!", rating: 5 }] },
    LinkList: { title: "Quick Links", links: [{ label: "Visit our website", url: "#" }] },
    RichText: { content: "Add your content here...", textAlign: "left" },
    ImageGallery: { title: "Gallery", images: [], layout: "grid" },
    VideoEmbed: { title: "", videoUrl: "", autoplay: false, aspectRatio: "16:9" },
    // ^ autoplay: use a direct .mp4/.webm URL and check autoplay in the editor
    Divider: { dividerStyle: "line", spacing: "md" },
    NavigationMenu: { mode: "auto", links: [] },
  };
  return defaults[type] || {};
};

/* 
   SORTABLE SECTION ITEM (sidebar list)
    */
function SortableItem({ id, section, isActive, onClick, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const meta = SECTION_META[section.type] || { label: section.type, icon: "Layers" };
  const IconComp = ICON_MAP[meta.icon] || Layers;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-2 px-3 py-2.5 rounded-xl cursor-pointer transition-all duration-200 group border ${
        isActive
          ? "bg-violet-500/15 border-violet-500/30 shadow-sm"
          : "border-transparent hover:bg-white/5 hover:border-zinc-700/50"
      }`}
      onClick={onClick}
    >
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing p-0.5 opacity-40 group-hover:opacity-70 transition-opacity"
      >
        <GripVertical size={14} className="text-zinc-400" />
      </button>
      <IconComp size={15} className={isActive ? "text-violet-400" : "text-zinc-500"} />
      <span className={`text-sm flex-1 truncate ${isActive ? "text-violet-300 font-medium" : "text-zinc-300"}`}>
        {meta.label}
      </span>
      <button
        onClick={(e) => { e.stopPropagation(); onDelete(); }}
        className="opacity-0 group-hover:opacity-60 hover:!opacity-100 p-1 rounded-lg hover:bg-red-500/20 transition-all"
      >
        <Trash2 size={13} className="text-red-400" />
      </button>
    </div>
  );
}

/* 
   URL PICKER  (Manual / Product / Upload)
    */

/** Pick the best image URL from a product object (all field shapes) */
function pickProductImg(p) {
  if (!p) return "";
  // Safely extract string URL from value that might be an object
  const s = (v) => {
    if (!v) return "";
    if (typeof v === "string") return v.trim();
    if (typeof v === "object" && !Array.isArray(v)) {
      const url = v.url || v.secure_url || v.path || v.src || v.image || "";
      return typeof url === "string" ? url.trim() : "";
    }
    return "";
  };
  return (
    s(p.image) ||
    s(p.imageUrl) ||
    s(p.thumbnail) ||
    s(p.photo) ||
    (Array.isArray(p.images) && p.images.length ? s(p.images[0]) : "") ||
    (Array.isArray(p.displayImages) && p.displayImages.length ? s(p.displayImages[0]) : "") ||
    s(p?.media?.image) ||
    (Array.isArray(p?.media?.images) && p.media.images.length ? s(p.media.images[0]) : "") ||
    // Fallback to variant images
    (() => {
      if (!Array.isArray(p.variants)) return "";
      for (const v of p.variants) {
        if (Array.isArray(v?.images)) {
          for (const img of v.images) {
            const url = s(img);
            if (url) return url;
          }
        }
      }
      return "";
    })() ||
    ""
  );
}

/**
 * Expand a product list so each variant appears as a separate entry,
 * useful inside the editor's Product tab image picker.
 */
function expandForPicker(products) {
  // One entry per parent product — variant selection happens on the detail page.
  const seen = new Set();
  const out = [];
  for (const p of Array.isArray(products) ? products : []) {
    const id = String(p._id || p.id || "");
    if (id && seen.has(id)) continue;
    if (id) seen.add(id);
    out.push(p);
  }
  return out;
}

function UrlPicker({ value, onChange, label = "Image URL", shopProducts = [], accept = "image/*" }) {
  const [tab, setTab] = useState("url");
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadError("");
    try {
      const res = await uploadShopMallAsset(file);
      const url = res?.url || res?.path || res?.secure_url || "";
      if (url) onChange(url);
      else setUploadError("Upload succeeded but no URL returned.");
    } catch (err) {
      setUploadError(err?.message || "Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const tabs = [
    { id: "url", label: "URL" },
    { id: "upload", label: "Upload" },
    { id: "product", label: "Product" },
  ];

  return (
    <div>
      <label className="text-xs text-zinc-400 mb-1.5 block font-medium">{label}</label>
      <div className="flex gap-1 mb-2">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`text-[10px] px-2.5 py-1 rounded-lg font-medium transition-all ${
              tab === t.id
                ? "bg-violet-500/20 text-violet-300"
                : "text-zinc-500 hover:text-zinc-300"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "url" && (
        <input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://..."
          className="w-full bg-zinc-800/50 text-sm text-zinc-200 border border-zinc-700/50 rounded-lg px-3 py-2 outline-none focus:border-violet-500/50 transition-colors"
        />
      )}

      {tab === "upload" && (
        <>
          <label className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 border-dashed border-zinc-700 hover:border-violet-500/50 cursor-pointer transition-colors">
            <ImageIcon size={16} className="text-zinc-500" />
            <span className="text-xs text-zinc-400">
              {uploading ? "Uploading..." : "Choose file"}
            </span>
            <input type="file" accept={accept} onChange={handleUpload} className="hidden" disabled={uploading} />
          </label>
          {uploadError && <p className="text-[10px] text-red-400 mt-1">{uploadError}</p>}
        </>
      )}

      {tab === "product" && (
        <div className="max-h-40 overflow-y-auto space-y-1 border border-zinc-700/50 rounded-lg p-2">
          {expandForPicker(shopProducts).slice(0, 60).map((p, idx) => {
            const img = pickProductImg(p);
            return (
              <button
                key={p._id || p.id || idx}
                onClick={() => {
                  if (img) onChange(toAbsUrl(img));
                }}
                className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left hover:bg-violet-500/10 transition-colors"
              >
                {img ? (
                  <img
                    src={toAbsUrl(img)}
                    alt=""
                    className="w-8 h-8 rounded object-cover flex-shrink-0 border border-zinc-700/50"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                ) : (
                  <div className="w-8 h-8 rounded bg-zinc-700/50 flex-shrink-0 flex items-center justify-center">
                    <ShoppingBag size={12} className="text-zinc-500" />
                  </div>
                )}
                <span className="text-xs text-zinc-300 truncate">
                  {p.title || p.name || "Product"}
                </span>
              </button>
            );
          })}
          {shopProducts.length === 0 && (
            <p className="text-xs text-zinc-500 text-center py-3">No products found</p>
          )}
        </div>
      )}

      {value && (
        <div className="mt-2 flex items-center gap-2">
          <img
            src={toAbsUrl(value)}
            alt="Preview"
            className="w-10 h-10 rounded-lg object-cover border border-zinc-700"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              e.currentTarget.nextSibling && (e.currentTarget.nextSibling.style.display = 'flex');
            }}
          />
          {/* Shown when image fails to load (e.g. a product page URL was pasted) */}
          <div
            className="w-10 h-10 rounded-lg border border-zinc-700 bg-zinc-800 items-center justify-center text-[9px] text-zinc-500 text-center leading-tight px-1"
            style={{ display: 'none' }}
          >
            Not an image
          </div>
          <button
            onClick={() => onChange("")}
            className="text-[10px] text-red-400 hover:text-red-300"
          >
            Remove
          </button>
        </div>
      )}
    </div>
  );
}

/* 
   PRODUCT PICKER (multi-select with search + variant support)
    */

/** Compute the storable ID for a picker entry: "parentId__variantId" or plain "parentId" */
function pickerEntryId(p) {
  if (p._parentId) {
    return p._variantId ? `${p._parentId}__${p._variantId}` : p._parentId;
  }
  return String(p._id || p.id || "");
}

function ProductPicker({ selectedIds = [], onChange, shopProducts = [], maxProducts = 12 }) {
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(false);
  const [selIndustry, setSelIndustry] = useState("");
  const [selCategory, setSelCategory] = useState("");

  // All entries including variants
  const allEntries = useMemo(() => expandForPicker(shopProducts), [shopProducts]);

  // Build industry → category tree for dropdowns
  const { industryList, categoryMap } = useMemo(() => {
    const map = {};
    for (const p of shopProducts) {
      const ind = String(p.industryName || p.industry || p.industryLabel || "Other").trim();
      const cat = String(p.categoryLabel || p.category || "Other").trim();
      if (!map[ind]) map[ind] = new Set();
      map[ind].add(cat);
    }
    return {
      industryList: Object.keys(map).sort(),
      categoryMap: Object.fromEntries(Object.entries(map).map(([k, v]) => [k, [...v].sort()])),
    };
  }, [shopProducts]);

  const categoryList = selIndustry ? (categoryMap[selIndustry] || []) : [];

  const filtered = useMemo(() => {
    let list = allEntries;
    if (selIndustry) {
      list = list.filter(p => {
        const ind = String(p.industryName || p.industry || p.industryLabel || "Other").trim();
        return ind === selIndustry;
      });
    }
    if (selCategory) {
      list = list.filter(p => {
        const cat = String(p.categoryLabel || p.category || "Other").trim();
        return cat === selCategory;
      });
    }
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(p => (p.title || p.name || "").toLowerCase().includes(q));
    }
    return list;
  }, [allEntries, selIndustry, selCategory, search]);

  // Resolve selected chips from the expanded list
  const selectedEntries = useMemo(() => {
    return selectedIds
      .map((id) => allEntries.find((p) => pickerEntryId(p) === id))
      .filter(Boolean);
  }, [selectedIds, allEntries]);

  const toggleEntry = (entry) => {
    const id = pickerEntryId(entry);
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((x) => x !== id));
    } else if (selectedIds.length < maxProducts) {
      onChange([...selectedIds, id]);
    }
  };

  const removeEntry = (id) => {
    onChange(selectedIds.filter((x) => x !== id));
  };

  return (
    <div>
      <label className="text-xs text-zinc-400 mb-1.5 block font-medium">
        Products ({selectedIds.length}/{maxProducts})
      </label>

      {/* Selected product chips */}
      {selectedEntries.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {selectedEntries.map((p) => {
            const id = pickerEntryId(p);
            const img = pickProductImg(p);
            return (
              <div
                key={id}
                className="flex items-center gap-1.5 bg-violet-500/15 border border-violet-500/30 rounded-lg px-2 py-1"
              >
                {img ? (
                  <img src={toAbsUrl(img)} alt="" className="w-5 h-5 rounded object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                ) : (
                  <ShoppingBag size={10} className="text-zinc-500" />
                )}
                <span className="text-[10px] text-violet-300 max-w-[90px] truncate">
                  {p.title || p.name}
                </span>
                <button
                  onClick={() => removeEntry(id)}
                  className="text-violet-400 hover:text-red-400 ml-0.5"
                >
                  <X size={10} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Dropdown toggle */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-3 py-2 rounded-lg border border-zinc-700/50 hover:border-violet-500/30 transition-colors text-sm text-zinc-400"
      >
        <span className="flex items-center gap-2">
          <ShoppingBag size={14} />
          {expanded ? "Close product picker" : "Choose products / variants"}
        </span>
        <ChevronDown size={14} className={`transition-transform ${expanded ? "rotate-180" : ""}`} />
      </button>

      {expanded && (
        <div className="mt-2 border border-zinc-700/50 rounded-xl overflow-hidden">
          {/* Industry + Category filters */}
          <div className="p-2 border-b border-zinc-700/50 flex gap-2">
            <select
              value={selIndustry}
              onChange={e => { setSelIndustry(e.target.value); setSelCategory(""); }}
              className="flex-1 min-w-0 px-2 py-1.5 bg-zinc-800/70 text-xs text-zinc-200 rounded-lg border border-zinc-700/50 outline-none focus:border-violet-500/50"
            >
              <option value="">All Industries</option>
              {industryList.map(ind => <option key={ind} value={ind}>{ind}</option>)}
            </select>
            <select
              value={selCategory}
              onChange={e => setSelCategory(e.target.value)}
              disabled={!selIndustry}
              className="flex-1 min-w-0 px-2 py-1.5 bg-zinc-800/70 text-xs text-zinc-200 rounded-lg border border-zinc-700/50 outline-none focus:border-violet-500/50 disabled:opacity-40"
            >
              <option value="">All Categories</option>
              {categoryList.map(cat => <option key={cat} value={cat}>{cat}</option>)}
            </select>
          </div>
          {/* Search */}
          <div className="p-2 border-b border-zinc-700/50">
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search products or variants..."
                className="w-full pl-8 pr-3 py-1.5 bg-zinc-800/50 text-xs text-zinc-200 rounded-lg border border-zinc-700/50 outline-none focus:border-violet-500/50"
              />
            </div>
          </div>

          {/* Product + variant list */}
          <div className="max-h-56 overflow-y-auto p-1.5 space-y-0.5">
            {filtered.length > 0 ? (
              filtered.map((p) => {
                const id = pickerEntryId(p);
                const isSelected = selectedIds.includes(id);
                const img = pickProductImg(p);
                const price = p.localPrice ?? p.price ?? "";
                const currency = String(p.currency || "USD").toUpperCase();
                const onSale = p.originalPrice && Number(p.originalPrice) > Number(price);
                const isVariant = Boolean(p._parentId && p._variantId);

                return (
                  <button
                    key={id}
                    onClick={() => toggleEntry(p)}
                    disabled={!isSelected && selectedIds.length >= maxProducts}
                    className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-left transition-all ${
                      isSelected
                        ? "bg-violet-500/15 border border-violet-500/30"
                        : "border border-transparent hover:bg-white/5 disabled:opacity-30"
                    } ${isVariant ? "pl-5" : ""}`}
                  >
                    <div className="w-9 h-9 rounded-lg overflow-hidden flex-shrink-0 bg-zinc-800">
                      {img ? (
                        <img src={toAbsUrl(img)} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <ShoppingBag size={14} className="text-zinc-600" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-xs truncate font-medium ${isVariant ? "text-zinc-300" : "text-zinc-200"}`}>
                        {isVariant && <span className="text-zinc-600 mr-1"></span>}
                        {p.title || p.name || "Product"}
                      </p>
                      <div className="flex items-center gap-1.5">
                        {price !== "" && (
                          <span className={`text-[10px] font-bold ${onSale ? "text-red-400" : "text-zinc-400"}`}>
                            {currency} {Number(price).toFixed(2)}
                          </span>
                        )}
                        {onSale && (
                          <span className="text-[10px] text-zinc-500 line-through">
                            {currency} {Number(p.originalPrice).toFixed(2)}
                          </span>
                        )}
                      </div>
                    </div>
                    <div
                      className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                        isSelected
                          ? "bg-violet-500 border-violet-500"
                          : "border-zinc-600"
                      }`}
                    >
                      {isSelected && <Check size={12} className="text-white" />}
                    </div>
                  </button>
                );
              })
            ) : (
              <div className="text-center py-6 text-xs text-zinc-500">
                {shopProducts.length === 0
                  ? "No products in your inventory yet"
                  : "No products match your search"}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* 
   SMART LINK PICKER  "  Industry  Category  Product drill-down
   Can be used anywhere a URL / product link is needed.
    */
function SmartLinkPicker({ value, onChange, shopProducts = [], shopId = "", label = "Link", mode = "url", customSidebar = [] }) {
  // mode="url"  produces /shop-mall/:shopId/product/:pid  or  ?industry=X&category=Y  or  ?customCat=indId:catId
  // mode="id"   produces parentId__variantId  (for ImageGallery productId field)
  const [pickerOpen, setPickerOpen] = useState(false);
  const [step, setStep] = useState("industry");
  const [selIndustry, setSelIndustry] = useState("");
  const [selCategory, setSelCategory] = useState("");

  // Build [industry][category] = [expanded entries]
  const tree = useMemo(() => {
    const map = {};
    expandForPicker(shopProducts).forEach((p) => {
      const ind = String(p.industryName || p.industry || p.industryLabel || "Other Industries").trim();
      const cat = String(p.categoryLabel || p.category || "Other").trim();
      if (!map[ind]) map[ind] = {};
      if (!map[ind][cat]) map[ind][cat] = [];
      const pid = p._parentId
        ? `${p._parentId}${p._variantId ? `__${p._variantId}` : ""}`
        : String(p._id || p.id || "");
      map[ind][cat].push({ ...p, _pid: pid });
    });
    return map;
  }, [shopProducts]);

  const industryList = useMemo(() => Object.keys(tree).sort(), [tree]);
  const categoryList = selIndustry ? Object.keys(tree[selIndustry] || {}).sort() : [];
  const productList = selIndustry && selCategory ? tree[selIndustry]?.[selCategory] || [] : [];

  const resetPicker = () => { setStep("industry"); setSelIndustry(""); setSelCategory(""); };

  const selectProduct = (p) => {
    const pid = p._pid;
    const result =
      mode === "id"
        ? pid
        : shopId
        ? `/shop-mall/${encodeURIComponent(shopId)}/product/${encodeURIComponent(pid)}`
        : pid;
    onChange(result);
    setPickerOpen(false);
    resetPicker();
  };

  // Select a whole product category → ?industry=X&category=Y
  const selectCategory = (ind, cat) => {
    const base = shopId ? `/shop-mall/${encodeURIComponent(shopId)}` : "";
    onChange(`${base}?industry=${encodeURIComponent(ind)}&category=${encodeURIComponent(cat)}`);
    setPickerOpen(false);
    resetPicker();
  };

  // Select a custom sidebar category → ?customCat=indId:catId
  const selectSidebarCat = (indId, catId) => {
    const base = shopId ? `/shop-mall/${encodeURIComponent(shopId)}` : "";
    onChange(`${base}?customCat=${encodeURIComponent(`${indId}:${catId}`)}`);
    setPickerOpen(false);
    resetPicker();
  };

  // Resolve a display label for whatever is currently stored
  const resolvedLabel = useMemo(() => {
    if (!value) return null;
    // Custom sidebar category link
    const csMatch = value.match(/[?&]customCat=([^&]+)/);
    if (csMatch) {
      const [indId, catId] = decodeURIComponent(csMatch[1]).split(":");
      for (const ind of customSidebar) {
        if (String(ind.id) === indId) {
          const cat = (ind.categories || []).find(c => String(c.id) === catId);
          if (cat) return { name: `${ind.industryName} › ${cat.name}`, img: null, icon: "sidebar" };
        }
      }
    }
    // Product category link
    const indMatch = value.match(/[?&]industry=([^&]+)/);
    const catMatch = value.match(/[?&]category=([^&]+)/);
    if (indMatch && catMatch) {
      return { name: `${decodeURIComponent(indMatch[1])} › ${decodeURIComponent(catMatch[1])}`, img: null, icon: "category" };
    }
    // Individual product link
    const all = expandForPicker(shopProducts);
    const match = all.find((p) => {
      const pid = p._parentId
        ? `${p._parentId}${p._variantId ? `__${p._variantId}` : ""}`
        : String(p._id || p.id || "");
      return mode === "id" ? value === pid : value.includes(pid);
    });
    return match ? { name: match.title || match.name, img: pickProductImg(match) } : null;
  }, [value, shopProducts, customSidebar, mode]);

  return (
    <div className="space-y-1.5">
      {label && <label className="text-xs text-zinc-400 font-medium block">{label}</label>}

      {/* Resolved chip — product, category, or sidebar section */}
      {resolvedLabel && (
        <div className={`flex items-center gap-2 px-2 py-1.5 rounded-lg border ${resolvedLabel.icon === "sidebar" ? "bg-amber-500/10 border-amber-500/20" : "bg-violet-500/10 border-violet-500/20"}`}>
          {resolvedLabel.img ? (
            <img src={toAbsUrl(resolvedLabel.img)} alt="" className="w-6 h-6 rounded object-cover flex-shrink-0" onError={(e) => { e.currentTarget.style.display = "none"; }} />
          ) : resolvedLabel.icon === "sidebar" ? (
            <Tag size={12} className="text-amber-400 flex-shrink-0" />
          ) : resolvedLabel.icon === "category" ? (
            <Layers size={12} className="text-violet-400 flex-shrink-0" />
          ) : null}
          <span className={`text-[11px] font-medium truncate flex-1 ${resolvedLabel.icon === "sidebar" ? "text-amber-300" : "text-violet-300"}`}>
            {resolvedLabel.name}
          </span>
          <button onClick={() => onChange("")} className="text-zinc-500 hover:text-red-400 transition-colors flex-shrink-0">
            <X size={11} />
          </button>
        </div>
      )}

      {/* Manual input + product picker button */}
      {mode === "url" && (
        <div className="flex gap-1.5">
          <input
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="https://... or pick a product "
            className="flex-1 bg-zinc-800/50 text-xs text-zinc-200 border border-zinc-700/50 rounded-lg px-2.5 py-2 outline-none focus:border-violet-500/50 transition-colors min-w-0"
          />
          <button
            type="button"
            onClick={() => { setPickerOpen(!pickerOpen); resetPicker(); }}
            title="Pick a product"
            className={`flex items-center gap-1 px-2.5 py-2 rounded-lg border text-[11px] font-medium transition-all flex-shrink-0 ${
              pickerOpen
                ? "bg-violet-500/20 border-violet-500/40 text-violet-300"
                : "border-zinc-700/50 text-zinc-400 hover:border-violet-500/30 hover:text-violet-300"
            }`}
          >
            <ShoppingBag size={12} /> Product
          </button>
        </div>
      )}
      {mode === "id" && (
        <button
          type="button"
          onClick={() => { setPickerOpen(!pickerOpen); resetPicker(); }}
          className={`w-full flex items-center justify-between px-3 py-2 rounded-lg border text-xs transition-all ${
            pickerOpen
              ? "bg-violet-500/20 border-violet-500/40 text-violet-300"
              : "border-zinc-700/50 text-zinc-400 hover:border-violet-500/30"
          }`}
        >
          <span className="flex items-center gap-2">
            <ShoppingBag size={13} />
            {resolvedLabel ? "Change product" : "Pick a product"}
          </span>
          <ChevronDown size={13} className={`transition-transform ${pickerOpen ? "rotate-180" : ""}`} />
        </button>
      )}

      {/* Drill-down panel */}
      {pickerOpen && (
        <div className="border border-zinc-700/50 rounded-xl overflow-hidden">

          {/* ── Sidebar / Discount sections — always visible ── */}
          {customSidebar.length > 0 && (
            <div className="border-b border-zinc-700/30">
              <p className="text-[9px] uppercase tracking-wider text-amber-600 font-bold px-3 pt-2 pb-1">
                🏷 Sidebar / Discount Sections
              </p>
              <div className="pb-1.5 px-1.5 space-y-0.5">
                {customSidebar.map(ind =>
                  (ind.categories || []).map(cat => (
                    <button
                      key={`${ind.id}:${cat.id}`}
                      onClick={() => selectSidebarCat(ind.id, cat.id)}
                      className="w-full flex items-center justify-between px-3 py-2 rounded-lg hover:bg-amber-500/10 border border-transparent hover:border-amber-500/20 text-left transition-colors"
                    >
                      <div>
                        <p className="text-xs text-zinc-200 font-semibold">{cat.name}</p>
                        <p className="text-[10px] text-zinc-500">{ind.industryName}{cat.discount > 0 ? ` · ${cat.discount}% off` : ""} · {(cat.productIds || []).length} items</p>
                      </div>
                      <Tag size={11} className="text-amber-500 flex-shrink-0" />
                    </button>
                  ))
                )}
              </div>
            </div>
          )}

          {/* ── Product / category drill-down ── */}
          {/* Breadcrumb */}
          <div className="px-3 py-2 bg-zinc-800/60 border-b border-zinc-700/30 flex items-center gap-1.5 text-[10px]">
            <p className="text-[9px] uppercase tracking-wider text-zinc-600 font-bold mr-1">Products:</p>
            <button
              onClick={() => { setStep("industry"); setSelIndustry(""); setSelCategory(""); }}
              className={`font-semibold transition-colors ${
                step === "industry" ? "text-zinc-200" : "text-violet-400 hover:underline"
              }`}
            >
              All
            </button>
            {selIndustry && (
              <>
                <span className="text-zinc-600">/</span>
                <button
                  onClick={() => { setStep("category"); setSelCategory(""); }}
                  className={`font-semibold transition-colors ${
                    step === "category" ? "text-zinc-200" : "text-violet-400 hover:underline"
                  }`}
                >
                  {selIndustry}
                </button>
              </>
            )}
            {selCategory && (
              <>
                <span className="text-zinc-600">/</span>
                <span className="text-zinc-300 font-semibold">{selCategory}</span>
              </>
            )}
          </div>

          <div className="max-h-48 overflow-y-auto p-1.5 space-y-0.5">
            {/* Step 1: Industries */}
            {step === "industry" && (<>
              {/* Regular industry categories */}
              {industryList.length > 0 ? (
                industryList.map((ind) => {
                  const catCount = Object.keys(tree[ind] || {}).length;
                  const total = Object.values(tree[ind] || {}).reduce((s, a) => s + a.length, 0);
                  return (
                    <button
                      key={ind}
                      onClick={() => { setSelIndustry(ind); setStep("category"); }}
                      className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-violet-500/10 text-left transition-colors group"
                    >
                      <span className="text-xs text-zinc-200 font-medium">{ind}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] text-zinc-500">{catCount} cats · {total} items</span>
                        <ChevronDown size={12} className="text-zinc-500 -rotate-90 group-hover:text-violet-400 transition-colors" />
                      </div>
                    </button>
                  );
                })
              ) : (
                <p className="text-xs text-zinc-500 text-center py-4">No products in inventory yet</p>
              )}
            </>)}

            {/* Step 2: Categories — with "link to whole category" option at top */}
            {step === "category" && (<>
              {/* Link to entire industry/category */}
              <button
                onClick={() => selectCategory(selIndustry, "")}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-violet-500/10 border border-violet-500/20 hover:bg-violet-500/15 text-left transition-colors mb-1"
              >
                <Layers size={13} className="text-violet-400 flex-shrink-0" />
                <div>
                  <p className="text-xs text-violet-300 font-semibold">All of "{selIndustry}"</p>
                  <p className="text-[10px] text-zinc-500">Link to all products in this industry</p>
                </div>
              </button>
              {categoryList.map((cat) => {
                const prods = tree[selIndustry]?.[cat] || [];
                const firstImg = pickProductImg(prods[0]);
                return (
                  <div key={cat} className="flex items-center gap-1">
                    <button
                      onClick={() => { setSelCategory(cat); setStep("products"); }}
                      className="flex-1 flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-violet-500/10 text-left transition-colors group"
                    >
                      {firstImg ? (
                        <img src={toAbsUrl(firstImg)} alt="" className="w-8 h-8 rounded-lg object-cover flex-shrink-0 border border-zinc-700/50" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                      ) : (
                        <div className="w-8 h-8 rounded-lg bg-zinc-800 flex items-center justify-center flex-shrink-0"><ShoppingBag size={13} className="text-zinc-600" /></div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-zinc-200 font-medium">{cat}</p>
                        <p className="text-[10px] text-zinc-500">{prods.length} item{prods.length !== 1 ? "s" : ""}</p>
                      </div>
                      <ChevronDown size={12} className="text-zinc-500 -rotate-90 group-hover:text-violet-400 flex-shrink-0 transition-colors" />
                    </button>
                    {/* Quick "link whole category" icon button */}
                    <button
                      onClick={() => selectCategory(selIndustry, cat)}
                      title={`Link to all "${cat}" products`}
                      className="p-2 rounded-lg text-zinc-600 hover:text-violet-400 hover:bg-violet-500/10 transition-colors flex-shrink-0"
                    >
                      <Layers size={12} />
                    </button>
                  </div>
                );
              })}
            </>)}

            {/* Step 3: Products & Variants */}
            {step === "products" &&
              productList.map((p, i) => {
                const img = pickProductImg(p);
                const price = p.localPrice ?? p.price ?? "";
                const currency = String(p.currency || "USD").toUpperCase();
                const isVariant = Boolean(p._parentId && p._variantId);
                return (
                  <button
                    key={p._pid || i}
                    onClick={() => selectProduct(p)}
                    className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg hover:bg-violet-500/10 text-left transition-colors group"
                  >
                    <div className="w-9 h-9 rounded-lg overflow-hidden flex-shrink-0 bg-zinc-800 border border-zinc-700/50">
                      {img ? (
                        <img
                          src={toAbsUrl(img)}
                          alt=""
                          className="w-full h-full object-cover"
                          onError={(e) => { e.currentTarget.style.display = "none"; }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <ShoppingBag size={13} className="text-zinc-600" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-xs font-medium truncate ${isVariant ? "text-zinc-300" : "text-zinc-200"}`}>
                        {isVariant && <span className="text-zinc-600 mr-1"></span>}
                        {p.title || p.name || "Product"}
                      </p>
                      {price !== "" && (
                        <p className="text-[10px] text-zinc-500">{currency} {Number(price).toFixed(2)}</p>
                      )}
                    </div>
                    <Check size={13} className="text-violet-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                  </button>
                );
              })
            }
          </div>
        </div>
      )}
    </div>
  );
}

/* 
   LINK LIST EDITOR
    */
function LinkListEditor({ links = [], onChange, shopProducts = [], shopId = "", customSidebar = [] }) {
  const updateLink = (index, field, value) => {
    const updated = [...links];
    updated[index] = { ...updated[index], [field]: value };
    onChange(updated);
  };
  const addLink = () => onChange([...links, { label: "New Link", url: "#" }]);
  const removeLink = (index) => onChange(links.filter((_, i) => i !== index));

  return (
    <div>
      <label className="text-xs text-zinc-400 mb-1.5 block font-medium">Links</label>
      <div className="space-y-3">
        {links.map((link, i) => (
          <div key={i} className="p-2.5 rounded-lg border border-zinc-700/30 bg-zinc-800/30 space-y-2">
            <div className="flex items-center gap-1.5">
              <input
                value={link.label || ""}
                onChange={(e) => updateLink(i, "label", e.target.value)}
                placeholder="Link label"
                className="flex-1 bg-zinc-800/50 text-xs text-zinc-200 border border-zinc-700/50 rounded-lg px-2.5 py-1.5 outline-none focus:border-violet-500/50"
              />
              <button
                onClick={() => removeLink(i)}
                className="p-1.5 text-red-400 hover:bg-red-500/10 rounded-lg flex-shrink-0"
              >
                <Trash2 size={12} />
              </button>
            </div>
            <SmartLinkPicker
              value={link.url || ""}
              onChange={(url) => updateLink(i, "url", url)}
              shopProducts={shopProducts}
              shopId={shopId}
              customSidebar={customSidebar}
              label=""
            />
          </div>
        ))}
      </div>
      <button
        onClick={addLink}
        className="mt-2 text-[10px] text-violet-400 hover:text-violet-300 flex items-center gap-1"
      >
        <Plus size={10} /> Add link
      </button>
    </div>
  );
}

/* 
   TESTIMONIALS EDITOR
    */
function TestimonialsEditor({ testimonials = [], onChange }) {
  const updateItem = (index, field, value) => {
    const updated = [...testimonials];
    updated[index] = { ...updated[index], [field]: value };
    onChange(updated);
  };
  const addItem = () =>
    onChange([...testimonials, { name: "Customer", text: "Great experience!", rating: 5 }]);
  const removeItem = (index) => onChange(testimonials.filter((_, i) => i !== index));

  return (
    <div>
      <label className="text-xs text-zinc-400 mb-1.5 block font-medium">Testimonials</label>
      <div className="space-y-3">
        {testimonials.map((item, i) => (
          <div key={i} className="p-3 rounded-lg border border-zinc-700/50 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-[10px] text-zinc-500 font-medium">#{i + 1}</span>
              <button onClick={() => removeItem(i)} className="text-red-400 hover:text-red-300">
                <Trash2 size={12} />
              </button>
            </div>
            <input
              value={item.name || ""}
              onChange={(e) => updateItem(i, "name", e.target.value)}
              placeholder="Customer name"
              className="w-full bg-zinc-800/50 text-xs text-zinc-200 border border-zinc-700/50 rounded-lg px-2.5 py-1.5 outline-none focus:border-violet-500/50"
            />
            <textarea
              value={item.text || item.quote || ""}
              onChange={(e) => updateItem(i, "text", e.target.value)}
              placeholder="Testimonial text"
              rows={2}
              className="w-full bg-zinc-800/50 text-xs text-zinc-200 border border-zinc-700/50 rounded-lg px-2.5 py-1.5 outline-none focus:border-violet-500/50 resize-none"
            />
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-zinc-500">Rating:</span>
              {[1, 2, 3, 4, 5].map((s) => (
                <button
                  key={s}
                  onClick={() => updateItem(i, "rating", s)}
                  className="transition-colors"
                >
                  <Star
                    size={14}
                    fill={s <= (item.rating || 0) ? "#fbbf24" : "none"}
                    stroke={s <= (item.rating || 0) ? "#fbbf24" : "#52525b"}
                  />
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
      <button
        onClick={addItem}
        className="mt-2 text-[10px] text-violet-400 hover:text-violet-300 flex items-center gap-1"
      >
        <Plus size={10} /> Add testimonial
      </button>
    </div>
  );
}

/* 
   IMAGE GALLERY EDITOR
    */
function ImageGalleryEditor({ images = [], onChange, shopProducts = [] }) {
  const addImage = () => onChange([...images, { url: "", productId: "", caption: "" }]);
  
  const removeImage = (index) => onChange(images.filter((_, i) => i !== index));
  
  const updateImage = (index, fieldOrFields, value) => {
    const updated = [...images];
    // Convert string format to object format if needed
    if (typeof updated[index] === "string") {
      updated[index] = { url: updated[index], productId: "", caption: "" };
    }
    if (typeof updated[index] !== "object") {
      updated[index] = { url: "", productId: "", caption: "" };
    }
    // Accept either a single field name + value, or an object of multiple fields
    if (typeof fieldOrFields === "object") {
      updated[index] = { ...updated[index], ...fieldOrFields };
    } else {
      updated[index] = { ...updated[index], [fieldOrFields]: value };
    }
    onChange(updated);
  };

  return (
    <div>
      <label className="text-xs text-zinc-400 mb-1.5 block font-medium">Image Gallery</label>
      <div className="space-y-4">
        {images.map((img, i) => {
          // Handle both old string format and new object format
          const imageData = typeof img === "string" 
            ? { url: img, productId: "", caption: "" }
            : { url: img?.url || "", productId: img?.productId || "", caption: img?.caption || "" };
          
          return (
            <div key={i} className="p-3 bg-zinc-800/50 rounded-lg border border-zinc-700/30">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-zinc-300 font-medium">Image {i + 1}</span>
                <button
                  onClick={() => removeImage(i)}
                  className="p-1 text-red-400 hover:bg-red-500/10 rounded text-xs"
                >
                  <Trash2 size={12} />
                </button>
              </div>
              
              {/* Image URL */}
              <div className="mb-2">
                <UrlPicker
                  value={imageData.url}
                  onChange={(url) => updateImage(i, "url", url)}
                  label="Image URL"
                  shopProducts={shopProducts}
                />
              </div>
              
              {/* Caption */}
              <div className="mb-2">
                <label className="text-xs text-zinc-400 mb-1 block">Caption (optional)</label>
                <input
                  value={imageData.caption}
                  onChange={(e) => updateImage(i, "caption", e.target.value)}
                  placeholder="Image caption"
                  className="w-full bg-zinc-800 text-xs text-zinc-200 border border-zinc-700/50 rounded-lg px-2 py-1 outline-none focus:border-violet-500/50"
                />
              </div>
              
              {/* Product Link */}
              <SmartLinkPicker
                value={imageData.productId}
                onChange={(pid) => {
                  // When a product is picked, also auto-fill the image URL if not already set
                  const fields = { productId: pid };
                  if (pid && !imageData.url) {
                    const expanded = expandForPicker(shopProducts);
                    const matched = expanded.find((p) => {
                      const epid = p._parentId
                        ? `${p._parentId}${p._variantId ? `__${p._variantId}` : ""}`
                        : String(p._id || p.id);
                      return epid === pid;
                    });
                    if (matched) {
                      const autoImg = pickProductImg(matched);
                      if (autoImg) fields.url = toAbsUrl(autoImg);
                    }
                  }
                  updateImage(i, fields);
                }}
                shopProducts={shopProducts}
                shopId=""
                label="Link to Product (optional)"
                mode="id"
              />
            </div>
          );
        })}
      </div>
      <button
        onClick={addImage}
        className="mt-3 text-xs text-violet-400 hover:text-violet-300 flex items-center gap-1.5 px-3 py-2 rounded-lg border border-zinc-700/30 hover:border-violet-500/30 transition-colors"
      >
        <Plus size={12} /> Add Image
      </button>
    </div>
  );
}

/* 
   NAVIGATION MENU EDITOR
    */
function NavigationMenuEditor({ props, onChange, shopProducts = [], shopId = "", customSidebar = [] }) {
  const mode = props?.mode || "auto";
  const links = Array.isArray(props?.links) ? props.links : [];

  // Build auto hierarchy from products
  const autoHierarchy = useMemo(() => {
    const hierarchy = {};
    (shopProducts || []).forEach((p) => {
      const ind = p.industryName || p.industry || p.industryLabel || "Other Industries";
      const cat = p.categoryLabel || p.category || "Other";
      if (!hierarchy[ind]) hierarchy[ind] = new Set();
      hierarchy[ind].add(cat);
    });
    return Object.entries(hierarchy)
      .sort()
      .map(([ind, cats]) => ({
        label: ind,
        children: Array.from(cats).sort().map((c) => ({ label: c, url: "#" })),
      }));
  }, [shopProducts]);

  return (
    <div className="space-y-3">
      <div>
        <label className="text-xs text-zinc-400 mb-1.5 block font-medium">Navigation Mode</label>
        <div className="flex gap-2">
          {["auto", "manual"].map((m) => (
            <button
              key={m}
              onClick={() => onChange({ ...props, mode: m })}
              className={`text-xs px-3 py-1.5 rounded-lg font-medium capitalize transition-all ${
                mode === m
                  ? "bg-violet-500/20 text-violet-300 border border-violet-500/30"
                  : "text-zinc-400 border border-zinc-700/50 hover:border-zinc-600"
              }`}
            >
              {m}
            </button>
          ))}
        </div>
      </div>

      {mode === "auto" && (
        <div className="p-3 rounded-lg border border-zinc-700/30 bg-zinc-800/30">
          <p className="text-[10px] text-zinc-500 mb-2">
            Auto-generated from your product categories:
          </p>
          {autoHierarchy.length > 0 ? (
            <div className="space-y-1">
              {autoHierarchy.map((item, i) => (
                <div key={i} className="text-xs text-zinc-300">
                  <span className="font-medium">{item.label}</span>
                  <span className="text-zinc-500 ml-1">
                    ({item.children.length} categories)
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-[10px] text-zinc-600">
              Add products with categories to auto-generate navigation
            </p>
          )}
        </div>
      )}

      {mode === "manual" && (
        <div className="space-y-2">
          {links.map((link, i) => (
            <div key={i} className="p-2 rounded-lg border border-zinc-700/30 bg-zinc-800/30 space-y-1.5">
              <div className="flex gap-1.5">
                <input
                  value={link.label || ""}
                  onChange={(e) => {
                    const updated = [...links];
                    updated[i] = { ...updated[i], label: e.target.value };
                    onChange({ ...props, links: updated });
                  }}
                  placeholder="Label"
                  className="flex-1 bg-zinc-800/50 text-xs text-zinc-200 border border-zinc-700/50 rounded-lg px-2.5 py-1.5 outline-none focus:border-violet-500/50"
                />
                <button
                  onClick={() => {
                    onChange({ ...props, links: links.filter((_, j) => j !== i) });
                  }}
                  className="p-1.5 text-red-400 hover:bg-red-500/10 rounded-lg"
                >
                  <Trash2 size={12} />
                </button>
              </div>
              <SmartLinkPicker
                value={link.url && link.url !== "#" ? link.url : ""}
                onChange={(url) => {
                  const updated = [...links];
                  updated[i] = { ...updated[i], url };
                  onChange({ ...props, links: updated });
                }}
                shopProducts={shopProducts}
                shopId={shopId}
                customSidebar={customSidebar}
                label=""
              />
            </div>
          ))}
          <button
            onClick={() => onChange({ ...props, links: [...links, { label: "New", url: "" }] })}
            className="text-[10px] text-violet-400 hover:text-violet-300 flex items-center gap-1"
          >
            <Plus size={10} /> Add nav item
          </button>
        </div>
      )}
    </div>
  );
}

/* 
   INPUT COMPONENTS FOR EDITOR FORM
    */
const FormInput = ({ field, label, placeholder, type: inputType = "text", value, onChange, ...rest }) => (
  <div>
    <label className="text-xs text-zinc-400 mb-1 block font-medium">{label}</label>
    <input
      type={inputType}
      value={value || ""}
      onChange={(e) => onChange(field, e.target.value)}
      placeholder={placeholder}
      className="w-full bg-zinc-800/50 text-sm text-zinc-200 border border-zinc-700/50 rounded-lg px-3 py-2 outline-none focus:border-violet-500/50 transition-colors"
      {...rest}
    />
  </div>
);

const FormTextArea = ({ field, label, placeholder, rows = 3, value, onChange }) => (
  <div>
    <label className="text-xs text-zinc-400 mb-1 block font-medium">{label}</label>
    <textarea
      value={value || ""}
      onChange={(e) => onChange(field, e.target.value)}
      placeholder={placeholder}
      rows={rows}
      className="w-full bg-zinc-800/50 text-sm text-zinc-200 border border-zinc-700/50 rounded-lg px-3 py-2 outline-none focus:border-violet-500/50 transition-colors resize-none"
    />
  </div>
);

const FormSelect = ({ field, label, options, value, onChange }) => (
  <div>
    <label className="text-xs text-zinc-400 mb-1 block font-medium">{label}</label>
    <select
      value={value || ""}
      onChange={(e) => onChange(field, e.target.value)}
      className="w-full bg-zinc-800/50 text-sm text-zinc-200 border border-zinc-700/50 rounded-lg px-3 py-2 outline-none focus:border-violet-500/50 cursor-pointer"
    >
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  </div>
);

/* 
   SECTION EDITOR FORM  "  renders correct fields per section type
    */
function EditorForm({
  section, onChange, shopProducts = [], shopInfo = {}, customSidebar = [],
  mallLogoUrl = "", setMallLogoUrl,
  uploadingLogo = false, setUploadingLogo,
  mallHeaderUrl = "", setMallHeaderUrl,
  uploadingHeader = false, setUploadingHeader,
}) {
  const { type, props = {} } = section || {};
  const shopId = String(shopInfo?._id || shopInfo?.id || "");

  // Use ref to track latest section without causing callback recreation
  const sectionRef = useRef(section);
  sectionRef.current = section;

  const update = useCallback((field, value) => {
    const currentSection = sectionRef.current;
    if (!currentSection) return;
    onChange({ ...currentSection, props: { ...currentSection.props, [field]: value } });
  }, [onChange]);

  if (!section) return null;

  const meta = SECTION_META[type] || { label: type };

  /* validate a URL — rejects garbage like "[object Object]" */
  const isValidImgUrl = (u) => typeof u === "string" && u.length > 4 && (u.startsWith("http") || u.startsWith("/"));
  const validLogoUrl = isValidImgUrl(mallLogoUrl) ? mallLogoUrl : "";
  const validHeaderUrl = isValidImgUrl(mallHeaderUrl) ? mallHeaderUrl : "";

  /* upload helper for branding images */
  const handleBrandUpload = async (e, setter, loadSetter) => {
    const file = e.target.files?.[0];
    if (!file) return;
    loadSetter(true);
    try {
      const res = await uploadShopMallAsset(file);
      const url = res?.url || res?.path || res?.secure_url || (typeof res === "string" ? res : "");
      if (url) setter(url);
      else alert("Upload succeeded but no URL returned.");
    } catch (err) {
      alert(err?.message || "Upload failed");
    } finally {
      loadSetter(false);
      e.target.value = "";
    }
  };

  return (
    <div className="space-y-4">
      {/* Section type header */}
      <div className="flex items-center gap-2 pb-3 border-b border-zinc-700/30">
        <Settings size={14} className="text-violet-400" />
        <span className="text-sm font-semibold text-zinc-200">{meta.label}</span>
      </div>

      {/* "" Profile Header "" */}
      {type === "ProfileHeader" && (
        <>
          <FormInput field="shopName" label="Shop Name Override" placeholder={shopInfo?.shopName || shopInfo?.name || "Leave empty to use profile name"} value={props.shopName} onChange={update} />
          <FormTextArea field="bio" label="Bio Override" placeholder={shopInfo?.bio || "Leave empty to use profile bio"} rows={2} value={props.bio} onChange={update} />

          {/* ── Mall Logo ── */}
          <div className="space-y-2 pt-1">
            <p className="text-xs font-semibold text-zinc-300">🖼 Mall Logo</p>
            <div className="flex items-center gap-3">
              {validLogoUrl ? (
                <div
                  className="relative w-14 h-14 rounded-full overflow-hidden border-2 border-zinc-700 flex-shrink-0 group"
                  style={{
                    backgroundImage: `url(${toAbsUrl(validLogoUrl)})`,
                    backgroundSize: props.logoFit === "contain" ? "contain" : "cover",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                  }}
                >
                  <button
                    onClick={() => setMallLogoUrl("")}
                    className="absolute inset-0 bg-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-white text-xs font-bold"
                  >✕</button>
                </div>
              ) : (
                <div className="w-14 h-14 rounded-full border-2 border-dashed border-zinc-700 flex items-center justify-center text-zinc-600 flex-shrink-0">
                  <User size={20} />
                </div>
              )}
              <label className="flex-1 cursor-pointer">
                <div className="px-3 py-2 rounded-lg border border-zinc-700 text-[11px] text-zinc-400 hover:text-zinc-200 hover:border-zinc-500 transition-colors text-center font-semibold">
                  {uploadingLogo ? "Uploading…" : validLogoUrl ? "Change Logo" : "Upload Logo"}
                </div>
                <input type="file" accept="image/*" className="hidden" disabled={uploadingLogo}
                  onChange={(e) => handleBrandUpload(e, setMallLogoUrl, setUploadingLogo)} />
              </label>
            </div>
            <FormSelect
              field="logoFit"
              label="Logo Fit"
              options={[
                { value: "cover", label: "Cover — fill circle" },
                { value: "contain", label: "Contain — show full logo" },
              ]}
              value={props.logoFit || "cover"}
              onChange={update}
            />
          </div>

          {/* ── Mall Header / Banner ── */}
          <div className="space-y-2 pt-1">
            <p className="text-xs font-semibold text-zinc-300">🖼 Mall Header / Banner</p>
            {validHeaderUrl ? (
              <div
                className="relative w-full h-24 rounded-lg overflow-hidden border border-zinc-700 group"
                style={{
                  backgroundImage: `url(${toAbsUrl(validHeaderUrl)})`,
                  backgroundSize: props.headerFit === "fill" ? "100% 100%" : (props.headerFit || "cover"),
                  backgroundPosition: props.headerPosition || "center",
                  backgroundRepeat: "no-repeat",
                }}
              >
                <button
                  onClick={() => setMallHeaderUrl("")}
                  className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/70 flex items-center justify-center text-white text-xs font-bold hover:bg-black/90 transition-colors"
                >✕</button>
              </div>
            ) : (
              <div className="w-full h-20 rounded-lg border-2 border-dashed border-zinc-700 flex items-center justify-center text-zinc-600">
                <ImageIcon size={20} />
              </div>
            )}
            <label className="cursor-pointer block">
              <div className="px-3 py-2 rounded-lg border border-zinc-700 text-[11px] text-zinc-400 hover:text-zinc-200 hover:border-zinc-500 transition-colors text-center font-semibold">
                {uploadingHeader ? "Uploading…" : validHeaderUrl ? "Change Header" : "Upload Header"}
              </div>
              <input type="file" accept="image/*" className="hidden" disabled={uploadingHeader}
                onChange={(e) => handleBrandUpload(e, setMallHeaderUrl, setUploadingHeader)} />
            </label>
            <FormSelect
              field="headerFit"
              label="Header Fit"
              options={[
                { value: "cover", label: "Cover — fill width (crop)" },
                { value: "contain", label: "Contain — show full image" },
                { value: "fill", label: "Fill — stretch to fit" },
              ]}
              value={props.headerFit || "cover"}
              onChange={update}
            />
            <FormSelect
              field="headerPosition"
              label="Header Position"
              options={[
                { value: "center", label: "Center" },
                { value: "top", label: "Top" },
                { value: "bottom", label: "Bottom" },
                { value: "left", label: "Left" },
                { value: "right", label: "Right" },
              ]}
              value={props.headerPosition || "center"}
              onChange={update}
            />
          </div>
        </>
      )}

      {/* "" Hero Banner "" */}
      {type === "HeroBanner" && (
        <>
          <FormInput field="title" label="Headline" placeholder="Welcome to our store" value={props.title} onChange={update} />
          <FormInput field="subtitle" label="Subtitle" placeholder="Discover amazing products" value={props.subtitle} onChange={update} />
          <UrlPicker value={props.imageUrl} onChange={(v) => update("imageUrl", v)} label="Background Image" shopProducts={shopProducts} />
          <FormInput field="buttonText" label="Button Text" placeholder="Shop Now" value={props.buttonText} onChange={update} />
          <SmartLinkPicker
            value={props.buttonLink || ""}
            onChange={(v) => update("buttonLink", v)}
            shopProducts={shopProducts}
            shopId={shopId}
            customSidebar={customSidebar}
            label="Button Link"
          />
        </>
      )}

      {/* "" Product Grid "" */}
      {type === "ProductGrid" && (
        <>
          <FormInput field="title" label="Section Title" placeholder="Our Products" value={props.title} onChange={update} />
          <ProductPicker
            selectedIds={Array.isArray(props.productIds) ? props.productIds : []}
            onChange={(ids) => update("productIds", ids)}
            shopProducts={shopProducts}
          />
        </>
      )}

      {/* "" Featured Product "" */}
      {type === "FeaturedProduct" && (
        <>
          <FormInput field="title" label="Title Override" placeholder="Featured" value={props.title} onChange={update} />
          <FormInput field="subtitle" label="Description Override" placeholder="" value={props.subtitle} onChange={update} />
          <ProductPicker
            selectedIds={Array.isArray(props.productIds) ? props.productIds : []}
            onChange={(ids) => update("productIds", ids)}
            shopProducts={shopProducts}
            maxProducts={1}
          />
          <FormInput field="badge" label="Badge Text" placeholder="SALE, NEW, etc." value={props.badge} onChange={update} />
          <FormInput field="buttonText" label="Button Text" placeholder="Shop Now" value={props.buttonText} onChange={update} />
          <SmartLinkPicker
            value={props.buttonLink || ""}
            onChange={(v) => update("buttonLink", v)}
            shopProducts={shopProducts}
            shopId={shopId}
            customSidebar={customSidebar}
            label="Button Link"
          />
        </>
      )}

      {/* "" Sale Banner "" */}
      {type === "SaleBanner" && (
        <>
          <FormInput field="title" label="Sale Title" placeholder="Big Sale!" value={props.title} onChange={update} />
          <FormInput field="subtitle" label="Subtitle" placeholder="Limited time offer" value={props.subtitle} onChange={update} />
          <FormInput field="discount" label="Discount Text" placeholder="Up to 50% OFF" value={props.discount} onChange={update} />
          <FormSelect
            field="bannerStyle"
            label="Banner Style"
            options={[
              { value: "gradient", label: "Fire Gradient" },
              { value: "bold", label: " Theme Color" },
              { value: "minimal", label: " Minimal" },
            ]}
            value={props.bannerStyle}
            onChange={update}
          />
          <UrlPicker value={props.imageUrl} onChange={(v) => update("imageUrl", v)} label="Background Image (optional)" shopProducts={shopProducts} />
          <FormInput field="buttonText" label="Button Text" placeholder="Shop Sale" value={props.buttonText} onChange={update} />
          <SmartLinkPicker
            value={props.buttonLink || ""}
            onChange={(v) => update("buttonLink", v)}
            shopProducts={shopProducts}
            shopId={shopId}
            customSidebar={customSidebar}
            label="Button Link"
          />
        </>
      )}

      {/* "" Announcement Bar "" */}
      {type === "AnnouncementBar" && (
        <>
          <FormInput field="text" label="Announcement Text" placeholder="Free shipping on orders over $50!" value={props.text} onChange={update} />
          <FormSelect
            field="barStyle"
            label="Style"
            options={[
              { value: "primary", label: "Primary (theme color)" },
              { value: "warning", label: "Warning (amber)" },
              { value: "info", label: "Info (blue)" },
              { value: "subtle", label: "Subtle (card bg)" },
            ]}
            value={props.barStyle}
            onChange={update}
          />
          <SmartLinkPicker
            value={props.link || ""}
            onChange={(v) => update("link", v)}
            shopProducts={shopProducts}
            shopId={shopId}
            customSidebar={customSidebar}
            label="Link (optional)"
          />
        </>
      )}

      {/* "" Countdown "" */}
      {type === "Countdown" && (
        <>
          <FormInput field="title" label="Title" placeholder=" Sale Ends In" value={props.title} onChange={update} />
          <FormInput field="endDate" label="End Date & Time" type="datetime-local" value={isoToLocalInput(props.endDate)} onChange={(f, v) => update(f, localInputToIso(v))} />
        </>
      )}

      {/* "" Testimonials "" */}
      {type === "Testimonials" && (
        <>
          <FormInput field="title" label="Section Title" placeholder="What Our Customers Say" value={props.title} onChange={update} />
          <TestimonialsEditor
            testimonials={Array.isArray(props.testimonials) ? props.testimonials : []}
            onChange={(items) => update("testimonials", items)}
          />
        </>
      )}

      {/* "" Link List "" */}
      {type === "LinkList" && (
        <>
          <FormInput field="title" label="Section Title" placeholder="Quick Links" value={props.title} onChange={update} />
          <LinkListEditor
            links={Array.isArray(props.links) ? props.links : []}
            onChange={(items) => update("links", items)}
            shopProducts={shopProducts}
            shopId={shopId}
            customSidebar={customSidebar}
          />
        </>
      )}

      {/* "" Rich Text "" */}
      {type === "RichText" && (
        <>
          <FormTextArea field="content" label="Content" placeholder="Write your content..." rows={5} value={props.content} onChange={update} />
          <FormSelect
            field="textAlign"
            label="Alignment"
            options={[
              { value: "left", label: "Left" },
              { value: "center", label: "Center" },
              { value: "right", label: "Right" },
            ]}
            value={props.textAlign}
            onChange={update}
          />
        </>
      )}

      {/* "" Image Gallery "" */}
      {type === "ImageGallery" && (
        <>
          <FormInput field="title" label="Gallery Title" placeholder="Gallery" value={props.title} onChange={update} />
          <FormSelect
            field="layout"
            label="Layout"
            options={[
              { value: "grid", label: "Grid (uniform)" },
              { value: "masonry", label: "Masonry (varied heights)" },
            ]}
            value={props.layout}
            onChange={update}
          />
          <ImageGalleryEditor
            images={Array.isArray(props.images) ? props.images : []}
            onChange={(items) => update("images", items)}
            shopProducts={shopProducts}
          />
        </>
      )}

      {/* "" Video Embed "" */}
      {type === "VideoEmbed" && (
        <>
          <FormInput field="title" label="Title (optional)" placeholder="Watch Our Story" value={props.title} onChange={update} />
          <UrlPicker
            label="Video URL (.mp4 for autoplay, or YouTube/Vimeo)"
            value={props.videoUrl || ""}
            onChange={(v) => update("videoUrl", v)}
            accept="video/mp4,video/webm,video/quicktime"
            shopProducts={[]}
          />

          <div className="flex items-center gap-2 p-3 rounded-lg border border-zinc-700/30 bg-zinc-800/30">
            <input
              type="checkbox"
              id="autoplay-toggle"
              checked={!!props.autoplay}
              onChange={(e) => update("autoplay", e.target.checked)}
              className="w-4 h-4 accent-violet-500 cursor-pointer"
            />
            <label htmlFor="autoplay-toggle" className="text-xs text-zinc-300 cursor-pointer select-none">
              <span className="font-semibold">Auto-play on open</span>
              <span className="text-zinc-500 ml-1">(direct .mp4 upload or URL - plays silently in a loop)</span>
            </label>
          </div>
          <p className="text-[10px] text-zinc-500">YouTube/Vimeo links still need a manual click to play.</p>
          <FormSelect
            field="aspectRatio"
            label="Video Shape"
            options={[
              { value: "16:9", label: "Wide (16:9) - default" },
              { value: "4:3",  label: "Classic (4:3)" },
              { value: "1:1",  label: "Square (1:1)" },
              { value: "9:16", label: "Portrait / Vertical (9:16)" },
              { value: "21:9", label: "Cinematic (21:9)" },
            ]}
            value={props.aspectRatio || "16:9"}
            onChange={update}
          />
        </>
      )}

      {/* "" Divider "" */}
      {type === "Divider" && (
        <>
          <FormSelect
            field="dividerStyle"
            label="Style"
            options={[
              { value: "line", label: "-- Line" },
              { value: "gradient", label: "--- Gradient" },
              { value: "dots", label: ". . . Dots" },
              { value: "space", label: "     Space" },
            ]}
            value={props.dividerStyle}
            onChange={update}
          />
          <FormSelect
            field="spacing"
            label="Spacing"
            options={[
              { value: "sm", label: "Small" },
              { value: "md", label: "Medium" },
              { value: "lg", label: "Large" },
            ]}
            value={props.spacing}
            onChange={update}
          />
        </>
      )}

      {/* "" Navigation Menu "" */}
      {type === "NavigationMenu" && (
        <NavigationMenuEditor
          props={props}
          onChange={(newProps) => onChange({ ...section, props: newProps })}
          shopProducts={shopProducts}
          shopId={shopId}
          customSidebar={customSidebar}
        />
      )}
    </div>
  );
}

/* 
   THEME GALLERY
    */
function ThemeGallery({ activeTheme, onSelect }) {
  // Group themes
  const groups = useMemo(() => {
    const g = {};
    Object.values(THEMES).forEach((t) => {
      const group = t.group || "Other";
      if (!g[group]) g[group] = [];
      g[group].push(t);
    });
    return g;
  }, []);

  return (
    <div className="space-y-5">
      {Object.entries(groups).map(([group, themes]) => (
        <div key={group}>
          <h3 className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-2 px-1">
            {group}
          </h3>
          <div className="grid grid-cols-2 gap-2">
            {themes.map((t) => {
              const isActive = activeTheme === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => onSelect(t.id)}
                  className={`relative p-3 rounded-xl border-2 transition-all duration-200 text-left group ${
                    isActive
                      ? "border-violet-500 shadow-lg shadow-violet-500/10"
                      : "border-zinc-700/50 hover:border-zinc-600"
                  }`}
                  style={{ background: t.bg }}
                >
                  {/* Mini preview */}
                  <div className="space-y-1.5">
                    <div
                      className="w-full h-2 rounded-full"
                      style={{ background: t.primary }}
                    />
                    <div className="flex gap-1">
                      <div
                        className="w-1/2 h-5 rounded"
                        style={{ background: t.card }}
                      />
                      <div
                        className="w-1/2 h-5 rounded"
                        style={{ background: t.card }}
                      />
                    </div>
                    <div
                      className="w-2/3 h-1.5 rounded-full"
                      style={{ background: t.border }}
                    />
                  </div>
                  <p
                    className="text-[10px] font-semibold mt-2"
                    style={{ color: t.text }}
                  >
                    {t.label}
                  </p>
                  <p className="text-[8px]" style={{ color: t.muted }}>
                    {t.navStyle === "side" ? "Side nav" : "Top nav"}
                  </p>

                  {isActive && (
                    <div className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-violet-500 rounded-full flex items-center justify-center">
                      <Check size={10} className="text-white" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

/* 
   SECTION PICKER (categorised add-section modal)
    */
function SectionPicker({ onAdd, onClose }) {
  const categories = useMemo(() => {
    const cats = {};
    Object.entries(SECTION_META).forEach(([type, meta]) => {
      const cat = meta.category || "other";
      if (!cats[cat]) cats[cat] = [];
      cats[cat].push({ type, ...meta });
    });
    return cats;
  }, []);

  const categoryLabels = {
    essentials: { label: "Essentials", icon: Sparkles },
    products: { label: "Products", icon: ShoppingBag },
    promotions: { label: "Promotions & Sales", icon: Flame },
    content: { label: "Content", icon: Type },
    layout: { label: "Layout", icon: Layers },
  };

  const categoryOrder = ["essentials", "products", "promotions", "content", "layout"];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-zinc-900 border border-zinc-700/50 rounded-2xl w-full max-w-lg max-h-[80vh] overflow-hidden shadow-2xl">
        {/* header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-700/30">
          <div>
            <h2 className="text-sm font-bold text-zinc-100">Add Section</h2>
            <p className="text-[10px] text-zinc-500 mt-0.5">Choose a section type to add</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/5 text-zinc-400 hover:text-zinc-200 transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        {/* categories */}
        <div className="overflow-y-auto p-4 space-y-5 max-h-[60vh]">
          {categoryOrder.map((catKey) => {
            const items = categories[catKey];
            if (!items) return null;
            const catInfo = categoryLabels[catKey] || { label: catKey, icon: Layers };
            const CatIcon = catInfo.icon;

            return (
              <div key={catKey}>
                <div className="flex items-center gap-2 mb-2">
                  <CatIcon size={13} className="text-zinc-500" />
                  <h3 className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">
                    {catInfo.label}
                  </h3>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {items.map(({ type, label, icon, description }) => {
                    const IconComp = ICON_MAP[icon] || Layers;
                    return (
                      <button
                        key={type}
                        onClick={() => {
                          onAdd(type);
                          onClose();
                        }}
                        className="flex items-start gap-3 p-3 rounded-xl border border-zinc-700/40 hover:border-violet-500/40 hover:bg-violet-500/5 text-left transition-all group"
                      >
                        <div className="w-8 h-8 rounded-lg bg-zinc-800 flex items-center justify-center flex-shrink-0 group-hover:bg-violet-500/20 transition-colors">
                          <IconComp size={16} className="text-zinc-400 group-hover:text-violet-400 transition-colors" />
                        </div>
                        <div>
                          <p className="text-xs font-medium text-zinc-200 group-hover:text-violet-300 transition-colors">
                            {label}
                          </p>
                          <p className="text-[10px] text-zinc-500 mt-0.5 leading-snug">
                            {description}
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}




/* 
   MAIN COMPONENT: ShopMallEdit
    */
/* ============================================================
   CustomSidebarEditor
   Lets shops build a custom sidebar with industries, categories,
   discount %, and product assignments.
   ============================================================ */
// Expand shopProducts into individual variant rows for the picker.
// Every variant (colour + size combination) gets its own selectable row.
function buildPickerRows(shopProducts) {
  const SIZE_RE = SHARED_SIZE_RE; // Single source of truth — see utils/sizeUtils.js
  const rows = [];
  for (const p of shopProducts) {
    const pid = String(p._id || p.id || "");
    const title = p.title || p.name || "Product";
    const baseImg = p.image || p.imageUrl || (Array.isArray(p.images) && p.images[0]) || "";
    const variants = Array.isArray(p.variants) ? p.variants.filter(v => v && v.isActive !== false) : [];

    const industry = String(p.industryName || p.industry || p.industryLabel || "").trim();
    const category = String(p.categoryLabel || p.category || "").trim();

    if (!variants.length) {
      // No variants — single row, id = productId
      rows.push({ id: pid, productId: pid, variantId: null, title, label: "", img: baseImg, industry, category });
      continue;
    }

    for (const v of variants) {
      const vid = String(v._id || "");
      if (!vid) continue;
      const ovs = Array.isArray(v.optionValues) ? v.optionValues : [];
      // Separate non-size values (colour, material…) from size values
      const nonSizeVals = ovs
        .filter(ov => !SIZE_RE.test(String(ov.value || "").trim()))
        .map(ov => String(ov.value || "").trim())
        .filter(Boolean);
      const sizeVals = ovs
        .filter(ov => SIZE_RE.test(String(ov.value || "").trim()))
        .map(ov => String(ov.value || "").trim())
        .filter(Boolean);
      // Label: "Blue / XS"  or just "XS" for size-only products
      const label = [...nonSizeVals, ...sizeVals].join(" / ");
      const variantImg = (Array.isArray(v.images) && v.images[0]) || baseImg;
      const rowId = `${pid}__${vid}`;
      rows.push({ id: rowId, productId: pid, variantId: vid, title, label, img: variantImg, industry, category });
    }
  }
  return rows;
}

function ProductPickerModal({ shopProducts, selectedIds, onClose, onSave, allSidebarProductIds = [] }) {
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState(new Set(selectedIds));
  const [selIndustry, setSelIndustry] = useState("");
  const [selCategory, setSelCategory] = useState("");

  // All rows: one per variant (or one per product if no variants)
  const allRows = useMemo(() => buildPickerRows(shopProducts), [shopProducts]);

  // Build industry → category tree for dropdowns
  const { industryList, categoryMap } = useMemo(() => {
    const map = {};
    for (const p of shopProducts) {
      const ind = String(p.industryName || p.industry || p.industryLabel || "Other").trim();
      const cat = String(p.categoryLabel || p.category || "Other").trim();
      if (!map[ind]) map[ind] = new Set();
      map[ind].add(cat);
    }
    return {
      industryList: Object.keys(map).sort(),
      categoryMap: Object.fromEntries(Object.entries(map).map(([k, v]) => [k, [...v].sort()])),
    };
  }, [shopProducts]);

  const categoryList = selIndustry ? (categoryMap[selIndustry] || []) : [];

  const filtered = allRows.filter(row => {
    if (selIndustry && row.industry !== selIndustry) return false;
    if (selCategory && row.category !== selCategory) return false;
    const text = `${row.title} ${row.label}`.toLowerCase();
    return !query || text.includes(query.toLowerCase());
  });

  const toggle = (id) => setPicked(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-md flex flex-col max-h-[80vh]">
        <div className="flex items-center justify-between p-4 border-b border-zinc-800">
          <h3 className="text-sm font-bold text-zinc-100">Pick Variants / Products</h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-300"><X size={16} /></button>
        </div>
        {/* Industry + Category filters */}
        <div className="px-3 pt-3 pb-2 border-b border-zinc-800 flex gap-2">
          <select
            value={selIndustry}
            onChange={e => { setSelIndustry(e.target.value); setSelCategory(""); }}
            className="flex-1 min-w-0 px-2 py-1.5 bg-zinc-800 text-xs text-zinc-200 rounded-lg border border-zinc-700 outline-none focus:border-violet-500"
          >
            <option value="">All Industries</option>
            {industryList.map(ind => <option key={ind} value={ind}>{ind}</option>)}
          </select>
          <select
            value={selCategory}
            onChange={e => setSelCategory(e.target.value)}
            disabled={!selIndustry}
            className="flex-1 min-w-0 px-2 py-1.5 bg-zinc-800 text-xs text-zinc-200 rounded-lg border border-zinc-700 outline-none focus:border-violet-500 disabled:opacity-40"
          >
            <option value="">All Categories</option>
            {categoryList.map(cat => <option key={cat} value={cat}>{cat}</option>)}
          </select>
        </div>
        {/* Search */}
        <div className="p-3 border-b border-zinc-800">
          <div className="flex items-center gap-2 bg-zinc-800 rounded-lg px-3 py-2">
            <Search size={14} className="text-zinc-500 flex-shrink-0" />
            <input
              autoFocus
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search by name, colour, size…"
              className="bg-transparent text-sm text-zinc-100 placeholder-zinc-500 outline-none flex-1"
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {filtered.length === 0 && (
            <p className="text-xs text-zinc-500 text-center py-6">No results</p>
          )}
          {filtered.map(row => {
            const isSelected = picked.has(row.id);
            const isInOther  = allSidebarProductIds.includes(row.id) && !selectedIds.includes(row.id);
            return (
              <button
                key={row.id}
                onClick={() => toggle(row.id)}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                  isSelected ? "bg-violet-500/20 border border-violet-500/40" : "hover:bg-zinc-800 border border-transparent"
                }`}
              >
                {row.img ? (
                  <img src={row.img} alt="" className="w-9 h-9 rounded object-cover flex-shrink-0" />
                ) : (
                  <div className="w-9 h-9 rounded bg-zinc-700 flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-zinc-200 truncate">{row.title}</p>
                  {row.label && (
                    <p className="text-[10px] text-violet-300 truncate">{row.label}</p>
                  )}
                  {isInOther && <p className="text-[10px] text-amber-400">Already in another category</p>}
                </div>
                {isSelected && <Check size={13} className="text-violet-400 flex-shrink-0" />}
              </button>
            );
          })}
        </div>
        <div className="p-3 border-t border-zinc-800 flex gap-2">
          <button onClick={onClose} className="flex-1 py-2 text-xs rounded-lg bg-zinc-800 text-zinc-300 hover:bg-zinc-700">Cancel</button>
          <button
            onClick={() => { onSave([...picked]); onClose(); }}
            className="flex-1 py-2 text-xs rounded-lg bg-violet-500 text-white hover:bg-violet-600 font-semibold"
          >
            Save ({picked.size} selected)
          </button>
        </div>
      </div>
    </div>
  );
}

function CustomSidebarEditor({ customSidebar, onChange, shopProducts, legacyCollections = [], onClearCollections }) {
  const [pickerState, setPickerState] = useState(null);
  const [expandedIndustry, setExpandedIndustry] = useState(null);
  const [newIndustryName, setNewIndustryName] = useState("");
  const [newCatNames, setNewCatNames] = useState({}); // { industryId: string }
  const [editingIndustry, setEditingIndustry] = useState(null); // { id, name }

  const simpleId = () => Math.random().toString(36).slice(2, 8);
  // Build once, not inside every category's render loop
  const allPickerRows = useMemo(() => buildPickerRows(shopProducts), [shopProducts]);

  const allAssignedIds = customSidebar.flatMap(ind =>
    ind.categories.flatMap(cat => cat.productIds || [])
  );

  const addIndustry = () => {
    if (!newIndustryName.trim()) return;
    const id = `ind-${simpleId()}`;
    onChange([...customSidebar, { id, industryName: newIndustryName.trim(), categories: [] }]);
    setExpandedIndustry(id);
    setNewIndustryName("");
  };

  const deleteIndustry = (id) => onChange(customSidebar.filter(i => i.id !== id));

  const renameIndustry = (id, name) => {
    if (!name?.trim()) return;
    onChange(customSidebar.map(i => i.id === id ? { ...i, industryName: name.trim() } : i));
    setEditingIndustry(null);
  };

  const addCategory = (industryId) => {
    const name = (newCatNames[industryId] || "").trim();
    if (!name) return;
    const catId = `cat-${simpleId()}`;
    onChange(customSidebar.map(ind => ind.id !== industryId ? ind : {
      ...ind,
      categories: [...ind.categories, { id: catId, name, discount: 0, productIds: [] }],
    }));
    setNewCatNames(prev => ({ ...prev, [industryId]: "" }));
  };

  const deleteCategory = (industryId, catId) =>
    onChange(customSidebar.map(ind => ind.id !== industryId ? ind : {
      ...ind, categories: ind.categories.filter(c => c.id !== catId),
    }));

  const updateCategoryDiscount = (industryId, catId, val) =>
    onChange(customSidebar.map(ind => ind.id !== industryId ? ind : {
      ...ind, categories: ind.categories.map(c => c.id !== catId ? c : {
        ...c, discount: Math.min(100, Math.max(0, Number(val) || 0)),
      }),
    }));

  const updateCategoryProducts = (industryId, catId, productIds) =>
    onChange(customSidebar.map(ind => ind.id !== industryId ? ind : {
      ...ind, categories: ind.categories.map(c => c.id !== catId ? c : { ...c, productIds }),
    }));

  return (
    <div className="space-y-3">
      <div className="p-3 bg-zinc-800/50 rounded-xl text-xs text-zinc-400 leading-relaxed">
        <p className="font-semibold text-zinc-300 mb-1">🗂 Mall Sidebar Builder</p>
        <p>Build your mall sidebar. Create sections (e.g. <em>Clearance</em>, <em>New Arrivals</em>) with categories inside (e.g. <em>50% off</em>, <em>New This Week</em>). Add products to each category and optionally set a discount % — automatically applied at checkout.</p>
      </div>

      {/* Inline Add Industry form — no prompt() */}
      <div className="flex gap-2">
        <input
          value={newIndustryName}
          onChange={e => setNewIndustryName(e.target.value)}
          onKeyDown={e => e.key === "Enter" && addIndustry()}
          placeholder="Section name (e.g. Clearance)"
          className="flex-1 px-3 py-2 text-xs bg-zinc-800 border border-zinc-700 rounded-lg text-zinc-100 placeholder-zinc-500 outline-none focus:border-violet-500"
        />
        <button
          onClick={addIndustry}
          disabled={!newIndustryName.trim()}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-violet-600 hover:bg-violet-500 text-white text-xs font-semibold disabled:opacity-40 transition-colors"
        >
          <FolderPlus size={13} /> Add
        </button>
      </div>

      {customSidebar.length === 0 && (
        <p className="text-xs text-zinc-600 text-center py-4">No sections yet. Type a name above and click Add.</p>
      )}

      {customSidebar.map(industry => {
        const isOpen = expandedIndustry === industry.id;
        const isEditing = editingIndustry?.id === industry.id;
        return (
          <div key={industry.id} className="border border-zinc-700 rounded-xl overflow-hidden">
            {/* Industry header */}
            <div className="flex items-center gap-2 px-3 py-2.5 bg-zinc-800">
              {isEditing ? (
                <input
                  autoFocus
                  value={editingIndustry.name}
                  onChange={e => setEditingIndustry({ ...editingIndustry, name: e.target.value })}
                  onKeyDown={e => { if (e.key === "Enter") renameIndustry(industry.id, editingIndustry.name); if (e.key === "Escape") setEditingIndustry(null); }}
                  onBlur={() => renameIndustry(industry.id, editingIndustry.name)}
                  className="flex-1 bg-zinc-700 border border-violet-500 rounded px-2 py-0.5 text-sm text-zinc-100 outline-none"
                />
              ) : (
                <button onClick={() => setExpandedIndustry(isOpen ? null : industry.id)} className="flex-1 flex items-center gap-2 text-left">
                  <ChevronRight size={14} className={`text-zinc-400 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                  <span className="text-sm font-semibold text-zinc-100">{industry.industryName}</span>
                  <span className="text-[10px] text-zinc-500 ml-1">({industry.categories.length})</span>
                </button>
              )}
              {!isEditing && (
                <button onClick={() => setEditingIndustry({ id: industry.id, name: industry.industryName })} className="p-1 text-zinc-500 hover:text-zinc-300 transition-colors" title="Rename">
                  <Settings size={13} />
                </button>
              )}
              <button onClick={() => deleteIndustry(industry.id)} className="p-1 text-zinc-500 hover:text-red-400 transition-colors" title="Delete">
                <Trash2 size={13} />
              </button>
            </div>

            {isOpen && (
              <div className="p-2 space-y-2">
                {industry.categories.map(cat => {
                  // Resolve human-readable label for each stored ID (productId or productId__variantId)
                  const pickedItems = (cat.productIds || []).map(pid => {
                    const row = allPickerRows.find(r => r.id === pid);
                    if (row) return { id: pid, label: row.label ? `${row.title} — ${row.label}` : row.title };
                    // fallback: just the id
                    return { id: pid, label: pid.slice(0, 16) + "…" };
                  });

                  return (
                    <div key={cat.id} className="bg-zinc-900 border border-zinc-700/60 rounded-lg p-2.5 space-y-2">
                      {/* Category header */}
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold text-zinc-200">{cat.name}</span>
                        <button onClick={() => deleteCategory(industry.id, cat.id)} className="p-0.5 text-zinc-500 hover:text-red-400 transition-colors" title="Delete category">
                          <Trash2 size={12} />
                        </button>
                      </div>

                      {/* Discount — toggle + input */}
                      <div className="bg-zinc-800/60 rounded-lg p-2 space-y-1.5">
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id={`disc-${cat.id}`}
                            checked={cat.discount > 0}
                            onChange={e => updateCategoryDiscount(industry.id, cat.id, e.target.checked ? (cat.discount || 10) : 0)}
                            className="w-3.5 h-3.5 accent-violet-500"
                          />
                          <label htmlFor={`disc-${cat.id}`} className="text-[11px] text-zinc-300 font-semibold cursor-pointer select-none">
                            Enable Discount
                          </label>
                        </div>
                        {cat.discount > 0 && (
                          <div className="flex items-center gap-2 pl-5">
                            <Percent size={11} className="text-orange-400 flex-shrink-0" />
                            <input
                              type="number"
                              min="1" max="100"
                              value={cat.discount}
                              onChange={e => updateCategoryDiscount(industry.id, cat.id, e.target.value)}
                              className="w-16 px-2 py-0.5 text-xs bg-zinc-700 border border-zinc-600 rounded text-zinc-100 outline-none focus:border-violet-500"
                            />
                            <span className="text-[11px] text-zinc-400">%</span>
                            <span className="text-[10px] text-orange-400 font-bold">{cat.discount}% OFF at checkout</span>
                          </div>
                        )}
                      </div>

                      {/* Products list with remove button */}
                      <div>
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-[11px] text-zinc-400">{pickedItems.length} item{pickedItems.length !== 1 ? "s" : ""}</span>
                          <button
                            onClick={() => setPickerState({ industryId: industry.id, categoryId: cat.id, currentIds: cat.productIds || [] })}
                            className="text-[10px] text-violet-400 hover:text-violet-300 font-semibold flex items-center gap-0.5"
                          >
                            <Plus size={11} /> Add / Edit
                          </button>
                        </div>
                        {pickedItems.length > 0 && (
                          <div className="space-y-1 max-h-32 overflow-y-auto">
                            {pickedItems.map(item => (
                              <div key={item.id} className="flex items-center justify-between gap-1 px-2 py-1 bg-zinc-800 rounded text-[10px] text-zinc-300">
                                <span className="truncate flex-1">{item.label}</span>
                                <button
                                  onClick={() => updateCategoryProducts(
                                    industry.id, cat.id,
                                    (cat.productIds || []).filter(pid => pid !== item.id)
                                  )}
                                  className="text-zinc-600 hover:text-red-400 flex-shrink-0 transition-colors"
                                  title="Remove"
                                >
                                  <X size={11} />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}

                {/* Inline Add Category form */}
                <div className="flex gap-1.5 pt-1">
                  <input
                    value={newCatNames[industry.id] || ""}
                    onChange={e => setNewCatNames(prev => ({ ...prev, [industry.id]: e.target.value }))}
                    onKeyDown={e => e.key === "Enter" && addCategory(industry.id)}
                    placeholder="Category name (e.g. 50% off)"
                    className="flex-1 px-2 py-1.5 text-[11px] bg-zinc-800 border border-zinc-700 rounded-lg text-zinc-100 placeholder-zinc-500 outline-none focus:border-violet-500"
                  />
                  <button
                    onClick={() => addCategory(industry.id)}
                    disabled={!(newCatNames[industry.id] || "").trim()}
                    className="px-2.5 py-1.5 rounded-lg bg-violet-600 hover:bg-violet-500 text-white text-[11px] font-semibold disabled:opacity-40 transition-colors"
                  >
                    <Plus size={12} />
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* Product picker modal */}
      {pickerState && (
        <ProductPickerModal
          shopProducts={shopProducts}
          selectedIds={pickerState.currentIds}
          allSidebarProductIds={allAssignedIds}
          onClose={() => setPickerState(null)}
          onSave={ids => updateCategoryProducts(pickerState.industryId, pickerState.categoryId, ids)}
        />
      )}

      {/* Legacy collections — show if any exist so shop can remove them */}
      {legacyCollections.length > 0 && (
        <div className="mt-4 border border-amber-500/30 rounded-xl overflow-hidden">
          <div className="flex items-center justify-between px-3 py-2 bg-amber-500/10">
            <div>
              <p className="text-xs font-semibold text-amber-400">⚠️ Old Collections ({legacyCollections.length})</p>
              <p className="text-[10px] text-zinc-500 mt-0.5">These are still visible in your mall. Remove them below.</p>
            </div>
            <button
              onClick={() => { if (window.confirm("Remove all old collections from your mall?")) onClearCollections(); }}
              className="text-[10px] px-2.5 py-1 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 font-semibold transition-colors"
            >
              Remove All
            </button>
          </div>
          <div className="p-2 space-y-1">
            {legacyCollections.map(col => (
              <div key={col.id} className="flex items-center justify-between px-3 py-1.5 bg-zinc-800/50 rounded-lg">
                <span className="text-xs text-zinc-300">{col.emoji} {col.label}</span>
                <span className="text-[10px] text-zinc-500">{(col.productIds || []).length} items</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function ShopMallEdit() {
  /* "" state "" */
  const [sections, setSections] = useState([]);
  const [themeId, setThemeId] = useState("midnight");
  const [pageBg, setPageBg] = useState("");
  const [mallLogoUrl, setMallLogoUrl] = useState("");
  const [mallHeaderUrl, setMallHeaderUrl] = useState("");
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingHeader, setUploadingHeader] = useState(false);
  const [shopInfo, setShopInfo] = useState({});
  const [activeId, setActiveId] = useState(null);
  const [tab, setTab] = useState("sections"); // sections | theme | edit | collections | sidebar
  const [collections, setCollections] = useState([]); // [{ id, label, emoji, productIds }]
  const [customSidebar, setCustomSidebar] = useState([]); // [{ id, industryName, categories: [{ id, name, discount, productIds }] }]
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [loading, setLoading] = useState(true);
  const [shopProducts, setShopProducts] = useState([]);
  const [isMobile, setIsMobile] = useState(true);
  const [showSectionPicker, setShowSectionPicker] = useState(false);
  const [hasUnsaved, setHasUnsaved] = useState(false);

  /* "" DnD sensors "" */
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  /* "" load data "" */
  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const [mallData, productsData] = await Promise.all([
          getMyShopMallPage(),
          loadAllShopProducts().catch(() => []),
        ]);

        if (cancelled) return;

        // Normalise products
        const prods = Array.isArray(productsData) ? productsData : [];
        setShopProducts(prods);

        // Mall page data
        const page = mallData?.mallPage || mallData || {};
        const rawSections = Array.isArray(page.sections) ? page.sections : [];

        // Normalise sections: ensure each has {id, type, props}
        const normalised = rawSections.map((s, i) => {
          const { id, type, ...rest } = s;
          const extra = rest.extra || {};
          // Merge top-level fields + extra into props
          const props = {
            ...rest,
            ...extra,
            ...(rest.props || {}),
          };
          // Rich Text is saved as { content: "..." }; the editor's text box needs the text
          // (it showed "[object Object]" after a reload).
          if (props.content && typeof props.content === "object") {
            props.content = String(props.content.content ?? props.content.text ?? props.content.html ?? "");
          }
          delete props.extra;
          delete props.props;
          return {
            id: id || `s-${i}-${simpleId()}`,
            type: type || "HeroBanner",
            props,
          };
        });

        setSections(normalised.length > 0 ? normalised : INITIAL_SECTIONS);
        setThemeId(page.themeId || "midnight");
        setPageBg(page.pageBg || "");
        const isValidUrl = (u) => typeof u === "string" && u.length > 4 && (u.startsWith("http") || u.startsWith("/"));
        setMallLogoUrl(isValidUrl(page.mallLogoUrl) ? page.mallLogoUrl : "");
        setMallHeaderUrl(isValidUrl(page.mallHeaderUrl) ? page.mallHeaderUrl : "");
        setCollections(Array.isArray(page.collections) ? page.collections : []);
        setCustomSidebar(Array.isArray(page.customSidebar) ? page.customSidebar : []);

        // Shop info
        const shop = mallData?.shop || mallData?.shopInfo || {};
        setShopInfo(shop);
      } catch {
        setSections(INITIAL_SECTIONS);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, []);

  /* "" mark unsaved on changes (skip the initial load population) "" */
  const initialLoadDone = useRef(false);
  useEffect(() => {
    if (!loading) {
      if (!initialLoadDone.current) { initialLoadDone.current = true; return; }
      setHasUnsaved(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections, themeId, pageBg, collections, customSidebar, mallLogoUrl, mallHeaderUrl]);

  /* "" save "" */
  const handleSave = useCallback(async () => {
    setSaving(true);
    setSaved(false);
    setSaveError("");
    try {
      const payload = {
        themeId: themeId || "midnight",
        pageBg: pageBg || THEMES[themeId]?.bg || "#09090b",
        coverUrl: "",
        mallLogoUrl: mallLogoUrl || "",
        mallHeaderUrl: mallHeaderUrl || "",
        collections,
        customSidebar,
        sections: sections.map((s) => ({
          id: s.id,
          type: s.type,
          order: s.order || 0,
          ...s.props,
          extra: s.props,
        })),
      };
      await updateMyShopMallPage(payload);
      setSaved(true);
      setHasUnsaved(false);
      window.dispatchEvent(new Event("mall-saved"));
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setSaveError(err?.message || "Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  }, [sections, themeId, pageBg, collections, customSidebar, mallLogoUrl, mallHeaderUrl]);

  /* "" DnD handler "" */
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setSections((prev) => {
      const oldIdx = prev.findIndex((s) => s.id === active.id);
      const newIdx = prev.findIndex((s) => s.id === over.id);
      return arrayMove(prev, oldIdx, newIdx);
    });
  };

  /* "" section CRUD "" */
  const addSection = (type) => {
    const newSection = {
      id: simpleId(),
      type,
      props: getDefaultProps(type),
    };
    setSections((prev) => [...prev, newSection]);
    setActiveId(newSection.id);
    setTab("edit");
  };

  const deleteSection = (id) => {
    setSections((prev) => prev.filter((s) => s.id !== id));
    if (activeId === id) setActiveId(null);
  };

  const updateSection = useCallback((updated) => {
    setSections((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
  }, []);

  const duplicateSection = (id) => {
    const original = sections.find((s) => s.id === id);
    if (!original) return;
    const copy = {
      ...original,
      id: simpleId(),
      props: { ...original.props },
    };
    const idx = sections.findIndex((s) => s.id === id);
    setSections((prev) => {
      const next = [...prev];
      next.splice(idx + 1, 0, copy);
      return next;
    });
  };

  /* "" active section "" */
  const activeSection = sections.find((s) => s.id === activeId);

  /* "" preview data "" */
  const previewData = useMemo(
    () => ({
      themeId,
      pageBg: pageBg || THEMES[themeId]?.bg || "#09090b",
      sections: sections.map((s) => {
        const base = { id: s.id, type: s.type, ...s.props, extra: s.props };
        /* Auto-inject shop profile avatar & cover for ProfileHeader */
        if (s.type === "ProfileHeader") {
          // Mall-specific logo/header takes priority over shop account images
          const avatar = mallLogoUrl || shopInfo?.logoUrl || shopInfo?.logo || "";
          const cover = mallHeaderUrl || shopInfo?.coverImage || "";
          return {
            ...base,
            avatarUrl: avatar,
            coverUrl: cover,
            shopName: s.props.shopName || shopInfo?.shopName || shopInfo?.name || "",
            bio: s.props.bio || shopInfo?.bio || "",
            logoFit: s.props.logoFit || "cover",
            headerFit: s.props.headerFit || "cover",
            headerPosition: s.props.headerPosition || "center",
          };
        }
        return base;
      }),
      shopInfo,
      products: shopProducts,
      collections,
      customSidebar,
    }),
    [sections, themeId, pageBg, shopInfo, shopProducts, mallLogoUrl, mallHeaderUrl, collections, customSidebar]
  );

  /* "" loading state "" */
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-zinc-950">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-zinc-500">Loading mall editor...</span>
        </div>
      </div>
    );
  }

  /* "" sidebar tabs "" */
  const sidebarTabs = [
    { id: "sections", label: "Sections", icon: Layers },
    { id: "theme", label: "Theme", icon: Palette },
    { id: "edit", label: "Edit", icon: Settings },
    { id: "sidebar", label: "Sidebar", icon: PanelLeft },
  ];

  return (
    <div className="flex h-screen bg-zinc-950 text-zinc-100 overflow-hidden">
      {/*  LEFT SIDEBAR  */}
      <aside className="w-80 flex-shrink-0 flex flex-col border-r border-zinc-800 bg-zinc-900/80">
        {/* Header */}
        <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
          <h1 className="text-sm font-bold text-zinc-100 flex items-center gap-2">
            <Sparkles size={16} className="text-violet-400" />
            Mall Builder
          </h1>
          <button
            onClick={handleSave}
            disabled={saving}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              saved
                ? "bg-green-500/20 text-green-400"
                : hasUnsaved
                ? "bg-violet-500 text-white hover:bg-violet-600 shadow-lg shadow-violet-500/20"
                : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700"
            }`}
          >
            {saving ? (
              <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : saved ? (
              <Check size={14} />
            ) : (
              <Save size={14} />
            )}
            {saving ? "Saving..." : saved ? "Saved!" : "Save"}
          </button>
        </div>

        {/* Save error */}
        {saveError && (
          <div className="px-4 py-2 text-[11px] text-red-400 bg-red-500/10 border-b border-red-500/20 flex items-center gap-1.5">
            <AlertCircle size={11} className="flex-shrink-0" />
            {saveError}
          </div>
        )}

        {/* Tabs */}
        <div className="flex border-b border-zinc-800">
          {sidebarTabs.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium transition-all border-b-2 ${
                tab === id
                  ? "text-violet-400 border-violet-500 bg-violet-500/5"
                  : "text-zinc-500 border-transparent hover:text-zinc-300"
              }`}
            >
              <Icon size={14} />
              {label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className="flex-1 overflow-y-auto p-3">
          {/* "" SECTIONS TAB "" */}
          {tab === "sections" && (
            <div className="space-y-3">
              {/* Section list */}
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={sections.map((s) => s.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-1">
                    {sections.map((section) => (
                      <SortableItem
                        key={section.id}
                        id={section.id}
                        section={section}
                        isActive={activeId === section.id}
                        onClick={() => {
                          setActiveId(section.id);
                          setTab("edit");
                        }}
                        onDelete={() => deleteSection(section.id)}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>

              {sections.length === 0 && (
                <div className="text-center py-8 text-xs text-zinc-500">
                  <Layers size={24} className="mx-auto mb-2 opacity-30" />
                  No sections yet. Add one below!
                </div>
              )}

              {/* Add section button */}
              <button
                onClick={() => setShowSectionPicker(true)}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 border-dashed border-zinc-700 hover:border-violet-500/50 hover:bg-violet-500/5 text-sm text-zinc-400 hover:text-violet-300 transition-all"
              >
                <Plus size={16} />
                Add Section
              </button>

              {/* Quick-add sale sections */}
              <div className="pt-2">
                <p className="text-[10px] uppercase tracking-wider text-zinc-600 font-semibold mb-2">
                  Quick Add
                </p>
                <div className="grid grid-cols-2 gap-1.5">
                  {[
                    { type: "SaleBanner", label: "Sale Banner", icon: Flame, color: "text-red-400" },
                    { type: "Countdown", label: "Countdown", icon: Clock, color: "text-amber-400" },
                    { type: "ProductGrid", label: "Products", icon: Grid3X3, color: "text-blue-400" },
                    { type: "AnnouncementBar", label: "Alert Bar", icon: Megaphone, color: "text-green-400" },
                  ].map(({ type, label, icon: Icon, color }) => (
                    <button
                      key={type}
                      onClick={() => addSection(type)}
                      className="flex items-center gap-1.5 px-2.5 py-2 rounded-lg border border-zinc-700/40 hover:border-zinc-600 hover:bg-white/3 text-xs text-zinc-400 hover:text-zinc-200 transition-all"
                    >
                      <Icon size={12} className={color} />
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* "" THEME TAB "" */}
          {tab === "theme" && (
            <ThemeGallery activeTheme={themeId} onSelect={setThemeId} />
          )}

          {/* "" EDIT TAB "" */}
          {tab === "edit" && (
            <>
              {activeSection ? (
                <div>
                  {/* Actions bar */}
                  <div className="flex items-center gap-1.5 mb-4">
                    <button
                      onClick={() => duplicateSection(activeSection.id)}
                      className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] text-zinc-400 hover:text-zinc-200 hover:bg-white/5 transition-colors border border-zinc-700/40"
                    >
                      <Copy size={11} /> Duplicate
                    </button>
                    <button
                      onClick={() => deleteSection(activeSection.id)}
                      className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-colors border border-zinc-700/40"
                    >
                      <Trash2 size={11} /> Delete
                    </button>
                  </div>

                  <EditorForm
                    section={activeSection}
                    onChange={updateSection}
                    shopProducts={shopProducts}
                    shopInfo={shopInfo}
                    customSidebar={customSidebar}
                    mallLogoUrl={mallLogoUrl}
                    setMallLogoUrl={setMallLogoUrl}
                    uploadingLogo={uploadingLogo}
                    setUploadingLogo={setUploadingLogo}
                    mallHeaderUrl={mallHeaderUrl}
                    setMallHeaderUrl={setMallHeaderUrl}
                    uploadingHeader={uploadingHeader}
                    setUploadingHeader={setUploadingHeader}
                  />
                </div>
              ) : (
                <div className="text-center py-12">
                  <Settings size={24} className="mx-auto mb-3 text-zinc-600" />
                  <p className="text-sm text-zinc-500 font-medium">No section selected</p>
                  <p className="text-xs text-zinc-600 mt-1">
                    Click a section in the Sections tab to edit it
                  </p>
                </div>
              )}
            </>
          )}
          {/* "" SIDEBAR TAB "" */}
          {tab === "sidebar" && (
            <CustomSidebarEditor
              customSidebar={customSidebar}
              onChange={setCustomSidebar}
              shopProducts={shopProducts}
              legacyCollections={collections}
              onClearCollections={() => setCollections([])}
            />
          )}
        </div>
      </aside>

      {/*  MAIN PREVIEW AREA  */}
      <main className="flex-1 flex flex-col bg-zinc-950 overflow-hidden">
        {/* Preview toolbar */}
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-zinc-800 bg-zinc-900/50">
          <div className="flex items-center gap-2">
            <Eye size={14} className="text-zinc-500" />
            <span className="text-xs text-zinc-500 font-medium">Live Preview</span>
            {hasUnsaved && (
              <span className="flex items-center gap-1 text-[10px] text-amber-400">
                <AlertCircle size={10} />
                Unsaved changes
              </span>
            )}
          </div>
          <div className="flex items-center gap-1 bg-zinc-800 rounded-lg p-0.5">
            <button
              onClick={() => setIsMobile(true)}
              className={`p-1.5 rounded-md transition-all ${
                isMobile
                  ? "bg-violet-500/20 text-violet-400"
                  : "text-zinc-500 hover:text-zinc-300"
              }`}
            >
              <Smartphone size={14} />
            </button>
            <button
              onClick={() => setIsMobile(false)}
              className={`p-1.5 rounded-md transition-all ${
                !isMobile
                  ? "bg-violet-500/20 text-violet-400"
                  : "text-zinc-500 hover:text-zinc-300"
              }`}
            >
              <Monitor size={14} />
            </button>
          </div>
        </div>

        {/* Preview frame */}
        <div className="flex-1 flex items-start justify-center p-6 overflow-y-auto">
          <div
            className={`bg-zinc-900 rounded-2xl overflow-hidden border border-zinc-800 shadow-2xl transition-all duration-500 ${
              isMobile ? "w-[390px]" : "w-full max-w-5xl"
            }`}
            style={{ minHeight: isMobile ? 700 : 600 }}
          >
            {/* Phone notch (mobile only) */}
            {isMobile && (
              <div className="flex items-center justify-center py-2 bg-black">
                <div className="w-28 h-5 bg-zinc-900 rounded-full" />
              </div>
            )}
            <div className={`overflow-y-auto ${isMobile ? "h-[680px]" : ""}`}>
              <ShopMallPreview
                previewData={previewData}
                hideEditControls={true}
                isMobile={isMobile}
              />
            </div>
          </div>
        </div>
      </main>

      {/* Section picker modal */}
      {showSectionPicker && (
        <SectionPicker
          onAdd={addSection}
          onClose={() => setShowSectionPicker(false)}
        />
      )}
    </div>
  );
}
